// ============================================
// Ably Connection Hook
// Handles Ably real-time connection with token auth
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import Ably from 'ably';
import { API_URL, getAnyValidToken } from '../../../utils/authUtils';

/**
 * Custom hook for managing Ably connection
 *
 * @param {string} tenant - Tenant slug ('nacme' or 'tla')
 * @param {boolean} enabled - Whether to connect
 * @returns {Object} Ably client and connection state
 */
export const useAbly = (tenant = 'nacme', enabled = true) => {
  const [client, setClient] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [error, setError] = useState(null);
  const clientRef = useRef(null);

  // Fetch token from backend
  const fetchToken = useCallback(async (tokenParams, callback) => {
    try {
      const token = getAnyValidToken();
      if (!token) {
        callback(new Error('No authentication token'), null);
        return;
      }

      const response = await fetch(`${API_URL}/api/chat/ably-token?tenant=${tenant}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        callback(new Error(errorData.detail || 'Failed to get Ably token'), null);
        return;
      }

      const tokenRequest = await response.json();
      callback(null, tokenRequest);
    } catch (err) {
      console.error('Error fetching Ably token:', err);
      callback(err, null);
    }
  }, [tenant]);

  // Initialize Ably connection
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const token = getAnyValidToken();
    if (!token) {
      setConnectionState('disconnected');
      setError('Not authenticated');
      return;
    }

    // Create Ably client with token auth
    const ablyClient = new Ably.Realtime({
      authCallback: fetchToken,
      autoConnect: true,
      echoMessages: false, // Don't echo messages back to sender
    });

    clientRef.current = ablyClient;

    // Suppress Ably "Channel detached" errors at the window level.
    // The Ably SDK internally throws _ErrorInfo("Channel detached") during
    // processMessage when a message arrives on a detached channel. This
    // happens during normal navigation/cleanup and is not actionable.
    const suppressAblyDetachErrors = (event) => {
      if (event?.error?.message === 'Channel detached' ||
          event?.message?.includes?.('Channel detached')) {
        event.preventDefault();
        return true;
      }
    };
    window.addEventListener('error', suppressAblyDetachErrors);
    window.addEventListener('unhandledrejection', (event) => {
      if (event?.reason?.message === 'Channel detached' ||
          event?.reason?.code === 90001) {
        event.preventDefault();
      }
    });

    // Catch unhandled "Channel detached" errors at the connection level.
    // These occur when Ably delivers a message to a channel that was
    // detached during navigation, token refresh, or React re-render.
    ablyClient.connection.on('error', (stateChange) => {
      const reason = stateChange?.reason;
      if (reason?.message?.includes('detached') || reason?.code === 90001) {
        // Expected during navigation/cleanup - suppress
        return;
      }
      console.warn('Ably connection error:', reason);
    });

    // Handle connection state changes
    ablyClient.connection.on('connected', () => {
      console.log('Ably connected');
      setConnectionState('connected');
      setError(null);
    });

    ablyClient.connection.on('connecting', () => {
      setConnectionState('connecting');
    });

    ablyClient.connection.on('disconnected', () => {
      console.log('Ably disconnected');
      setConnectionState('disconnected');
    });

    ablyClient.connection.on('suspended', () => {
      setConnectionState('suspended');
    });

    ablyClient.connection.on('failed', (stateChange) => {
      console.error('Ably connection failed:', stateChange.reason);
      setConnectionState('failed');
      setError(stateChange.reason?.message || 'Connection failed');
    });

    setClient(ablyClient);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('error', suppressAblyDetachErrors);
      if (clientRef.current) {
        clientRef.current.close();
        clientRef.current = null;
      }
    };
  }, [enabled, fetchToken]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.connect();
    }
  }, []);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.close();
    }
  }, []);

  return {
    client,
    connectionState,
    isConnected: connectionState === 'connected',
    isConnecting: connectionState === 'connecting',
    error,
    reconnect,
    disconnect
  };
};

/**
 * Hook for subscribing to an Ably channel
 *
 * @param {Ably.Realtime} client - Ably client instance
 * @param {string} channelName - Ably channel name
 * @param {Function} onMessage - Message handler callback
 * @returns {Object} Channel instance and state
 */
export const useChannel = (client, channelName, onMessage) => {
  const [channel, setChannel] = useState(null);
  const [channelState, setChannelState] = useState('detached');
  const channelRef = useRef(null);
  const onMessageRef = useRef(onMessage);

  // Keep callback ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!client || !channelName) {
      return;
    }

    let isCleanedUp = false;
    const ablyChannel = client.channels.get(channelName);
    channelRef.current = ablyChannel;
    setChannel(ablyChannel);

    // Subscribe to all events
    const messageHandler = (message) => {
      // Don't process messages if we've cleaned up
      if (isCleanedUp) return;
      if (onMessageRef.current) {
        onMessageRef.current(message);
      }
    };

    try {
      ablyChannel.subscribe(messageHandler);
    } catch (err) {
      console.warn('Failed to subscribe to channel:', channelName, err);
    }

    // Track channel state
    const handleAttached = () => !isCleanedUp && setChannelState('attached');
    const handleDetached = (stateChange) => {
      if (!isCleanedUp) {
        // Only log if it's an unexpected detach (not from cleanup)
        if (stateChange?.reason) {
          console.debug('Channel detached:', channelName, stateChange.reason.message);
        }
        setChannelState('detached');
      }
    };
    const handleFailed = (stateChange) => {
      if (!isCleanedUp) {
        console.warn('Channel failed:', channelName, stateChange?.reason);
        setChannelState('failed');
      }
    };

    // Handle channel errors to prevent unhandled exceptions
    const handleError = (err) => {
      // Suppress "Channel detached" errors - these are expected during navigation/cleanup
      if (err?.message?.includes('detached') || err?.code === 90001) {
        console.debug('Channel error (safe to ignore):', err.message);
        return;
      }
      console.warn('Channel error:', channelName, err);
    };

    ablyChannel.on('attached', handleAttached);
    ablyChannel.on('detached', handleDetached);
    ablyChannel.on('failed', handleFailed);
    ablyChannel.on('error', handleError);

    return () => {
      isCleanedUp = true;
      try {
        ablyChannel.off('attached', handleAttached);
        ablyChannel.off('detached', handleDetached);
        ablyChannel.off('failed', handleFailed);
        ablyChannel.off('error', handleError);
        ablyChannel.unsubscribe(messageHandler);
        // Only detach if the channel is attached
        if (ablyChannel.state === 'attached') {
          ablyChannel.detach().catch(err => {
            // Ignore detach errors - channel may already be detaching
            console.debug('Channel detach warning (safe to ignore):', err.message);
          });
        }
      } catch (err) {
        // Ignore cleanup errors
        console.debug('Channel cleanup warning (safe to ignore):', err.message);
      }
    };
  }, [client, channelName]);

  // Publish message to channel
  const publish = useCallback(async (eventName, data) => {
    if (!channelRef.current) {
      throw new Error('Channel not initialized');
    }
    await channelRef.current.publish(eventName, data);
  }, []);

  return {
    channel,
    channelState,
    isAttached: channelState === 'attached',
    publish
  };
};

/**
 * Hook for presence on a channel
 *
 * @param {Ably.Types.RealtimeChannelCallbacks} channel - Ably channel
 * @returns {Object} Presence members and functions
 */
export const usePresence = (channel) => {
  const [members, setMembers] = useState([]);
  const [isEntered, setIsEntered] = useState(false);

  useEffect(() => {
    if (!channel) return;

    // Get initial presence
    channel.presence.get((err, presenceMembers) => {
      if (!err && presenceMembers) {
        setMembers(presenceMembers);
      }
    });

    // Subscribe to presence changes
    const handleEnter = (member) => {
      setMembers(prev => [...prev.filter(m => m.clientId !== member.clientId), member]);
    };

    const handleLeave = (member) => {
      setMembers(prev => prev.filter(m => m.clientId !== member.clientId));
    };

    const handleUpdate = (member) => {
      setMembers(prev => prev.map(m => m.clientId === member.clientId ? member : m));
    };

    channel.presence.subscribe('enter', handleEnter);
    channel.presence.subscribe('leave', handleLeave);
    channel.presence.subscribe('update', handleUpdate);

    return () => {
      channel.presence.unsubscribe('enter', handleEnter);
      channel.presence.unsubscribe('leave', handleLeave);
      channel.presence.unsubscribe('update', handleUpdate);
    };
  }, [channel]);

  // Enter presence
  const enter = useCallback(async (data) => {
    if (!channel) return;
    await channel.presence.enter(data);
    setIsEntered(true);
  }, [channel]);

  // Leave presence
  const leave = useCallback(async () => {
    if (!channel) return;
    await channel.presence.leave();
    setIsEntered(false);
  }, [channel]);

  // Update presence data
  const update = useCallback(async (data) => {
    if (!channel) return;
    await channel.presence.update(data);
  }, [channel]);

  return {
    members,
    isEntered,
    enter,
    leave,
    update
  };
};

export default useAbly;
